import { useEffect, useMemo, useState } from 'react';
import {
    Box, Typography, TextField, Chip, Stack, Accordion, AccordionSummary, AccordionDetails,
    Table, TableBody, TableCell, TableHead, TableRow, CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { getSegmentTree, getSegmentDataPoints } from '../../../services/integrationHubService';
import { providerKey } from './constants';

// The live segment tree for whichever of this client's data sources actually
// belong to this provider — matched by name, since a data source isn't
// tagged with a provider id anywhere upstream — plus this client's own note
// on how THEIR connection specifically works (category/what-it-is describe
// the provider itself and live on ProviderInfoCard instead, once per
// provider rather than repeated on every client).

function SegmentRow({ segment }) {
    const [open, setOpen] = useState(false);
    const [points, setPoints] = useState(null); // null = not loaded yet
    const [error, setError] = useState(null);

    const toggle = () => {
        const next = !open;
        setOpen(next);
        if (next && points === null) {
            getSegmentDataPoints(segment.segment_id)
                .then(setPoints)
                .catch((e) => setError(e.message));
        }
    };

    return (
        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" spacing={1.25} alignItems="center" onClick={toggle}
                sx={{ py: 1, px: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
                <ExpandMoreIcon sx={{ fontSize: 18, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s', color: 'text.secondary' }} />
                <Typography sx={{ fontSize: 13, fontWeight: 600, flex: 1 }}>{segment.name}</Typography>
                {segment.suffix && <Chip label={segment.suffix} size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }} />}
            </Stack>
            {open && (
                <Box sx={{ pb: 1.5, px: 1 }}>
                    {error && <Typography sx={{ color: '#c62828', fontSize: 12 }}>{error}</Typography>}
                    {!error && points === null && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}><CircularProgress size={18} /></Box>
                    )}
                    {points && points.length === 0 && (
                        <Typography sx={{ fontSize: 12, color: 'text.secondary', py: 1 }}>No datapoints defined for this segment.</Typography>
                    )}
                    {points && points.length > 0 && (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {['Column', 'Type', 'PK'].map((h) => (
                                        <TableCell key={h} sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary', py: 0.5 }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {points.map((dp) => (
                                    <TableRow key={dp.NexusDataPoint_Id}>
                                        <TableCell sx={{ fontSize: 12, py: 0.5 }}>{dp.ActualName}</TableCell>
                                        <TableCell sx={{ fontSize: 12, py: 0.5 }}>{dp.DataType || '—'}</TableCell>
                                        <TableCell sx={{ py: 0.5 }}>{dp.IsPrimaryKey ? <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} /> : null}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </Box>
            )}
        </Box>
    );
}

function DataSourceAccordion({ ds }) {
    const segmentCount = useMemo(
        () => (ds?.partitions ?? []).reduce((n, p) => n + (p.segments?.length ?? 0), 0),
        [ds],
    );
    return (
        <Accordion variant="outlined" disableGutters sx={{ borderRadius: 2, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flex: 1, pr: 1 }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 700, flex: 1 }}>{ds.data_source_title}</Typography>
                    <Chip label={`${segmentCount} segment${segmentCount === 1 ? '' : 's'}`} size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
                {(ds.partitions ?? []).map((p) => (
                    <Box key={p.partition_id} sx={{ mb: 1 }}>
                        <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: 'text.secondary', px: 1, py: 0.5, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                            {p.partition_title}
                        </Typography>
                        {(p.segments ?? []).map((s) => <SegmentRow key={s.segment_id} segment={s} />)}
                    </Box>
                ))}
            </AccordionDetails>
        </Accordion>
    );
}

const SchemaTab = ({ clientId, matchKey, schema, onSchemaChange }) => {
    const [tree, setTree] = useState(null); // null = not loaded yet
    const [error, setError] = useState(null);

    useEffect(() => {
        let alive = true;
        setTree(null);
        getSegmentTree(clientId)
            .then((t) => { if (alive) setTree(t); })
            .catch((e) => { if (alive) setError(e.message); });
        return () => { alive = false; };
    }, [clientId]);

    const matched = useMemo(
        () => (tree || []).filter((ds) => providerKey(ds.data_source_title) === matchKey),
        [tree, matchKey],
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
                size="small" label="How we connect for this client" fullWidth multiline minRows={2} maxRows={12}
                placeholder="Anything specific to this client's connection — credentials setup, quirks, contacts"
                value={schema?.howWeConnect || ''}
                onChange={(e) => onSchemaChange({ ...schema, howWeConnect: e.target.value })}
            />

            <Box>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', mb: 0.75, letterSpacing: 0.3 }}>LIVE SEGMENTS</Typography>
                {error && <Typography sx={{ color: '#c62828', fontSize: 12.5 }}>{error}</Typography>}
                {!error && tree === null && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={20} /></Box>
                )}
                {tree !== null && matched.length === 0 && (
                    <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                        No live segments matched to this provider yet for this client.
                    </Typography>
                )}
                <Stack spacing={1}>
                    {matched.map((ds) => <DataSourceAccordion key={ds.data_source_id} ds={ds} />)}
                </Stack>
            </Box>
        </Box>
    );
};

export default SchemaTab;
